/* Hallmark · component: auth-forgot-password · genre: modern-minimal · theme: project system
 * states: default · hover · focus · active · disabled · loading · error · success
 * pre-emit critique: P4 H5 E4 S5 R5 V4
 */

/**
 * ForgotPasswordPage — pdf-api's own reset-request screen.
 *
 * Wired through `config.auth.components.forgotPassword`.
 *
 * The security behaviour is deliberate and inherited from the kernel's version:
 * the success state shows whether or not the address exists, so the screen never
 * reveals which emails are registered. Don't "fix" the empty catch.
 *
 * Redesign vs. the previous version of this file (which was a dead, drifted copy
 * of the kernel's): both states are left-aligned now, and the success state no
 * longer centres a 12×12 green tick — the confirmation sentence is the point.
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { getAuthClient } from '@xhubio-saas/core-frontend/kernel'
import { Button, cn, Input, Label } from '@xhubio-saas/core-frontend/ui-components'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { z } from 'zod'
import { AuthAlert, AuthHeading, CONTROL_TRANSITION, FIELD_RING } from './authUi'

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({ resolver: zodResolver(forgotPasswordSchema) })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      await getAuthClient().requestPasswordReset({
        email: data.email,
        redirectTo: `${window.location.origin}/reset-password`,
      })
      setSuccess(true)
    } catch {
      // Always report success: telling the user "no such account" would turn
      // this form into an account-enumeration oracle.
      setSuccess(true)
    }
  }

  const backToLogin = (
    <div className="flex">
      <Link
        to="/login"
        className={cn(
          'inline-flex items-center gap-2 rounded-sm text-sm text-muted-foreground',
          'hover:text-foreground hover:underline underline-offset-4',
          CONTROL_TRANSITION,
          FIELD_RING
        )}
      >
        <ArrowLeft aria-hidden className="h-4 w-4" />
        {t('auth.backToLogin', 'Back to login')}
      </Link>
    </div>
  )

  if (success) {
    return (
      <div className="flex w-full flex-col gap-8">
        <AuthHeading title={t('auth.checkEmail', 'Check your email')} />
        <AuthAlert tone="success">
          {t('auth.resetEmailSent', "We've sent you an email with a link to reset your password.")}
        </AuthAlert>
        {backToLogin}
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-8">
      <AuthHeading
        title={t('auth.forgotPassword', 'Forgot password?')}
        description={t(
          'auth.forgotPasswordDescription',
          "Enter your email and we'll send you a reset link"
        )}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">{t('auth.email', 'Email')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="name@example.com"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-error' : undefined}
            className={cn(FIELD_RING, errors.email && 'border-destructive')}
            {...register('email')}
          />
          {errors.email && (
            <p id="email-error" className="text-xs text-destructive">
              {errors.email.message}
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className={cn(
            'h-11 w-full gap-2',
            'transition-[background-color,transform] duration-[var(--dur-micro)] ease-[var(--ease-out)]',
            'active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          {isSubmitting && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
          {t('auth.sendResetLink', 'Send reset link')}
        </Button>
      </form>

      {backToLogin}
    </div>
  )
}
